import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import {
  Users,
  Briefcase,
  Building2,
  DollarSign,
  Heart,
  ChevronRight,
  MapPin,
  TrendingUp,
  Wallet,
} from "lucide-react";

const DashboardOverview = () => {
  const { user } = useAuth();
  const [savedProperties, setSavedProperties] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.user_id) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchDashboardData = async () => {
    if (!user?.user_id) {
      setLoading(false);
      return;
    }
    try {
      const savedRes = await axios.get(`${API}/saved-properties`, {
        withCredentials: true
      });
      setSavedProperties(savedRes.data || []);

      if (user?.role === "agent" || user?.role === "admin" || user?.role === "ceo") {
        const statsRes = await axios.get(`${API}/crm/stats`, {
          withCredentials: true
        });
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setSavedProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, listingType) => {
    if (listingType === "rent") return `K${price.toLocaleString()}/mo`;
    return `K${price.toLocaleString()}`;
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-serif mb-2">Welcome back, {user?.name?.split(' ')[0]}!</h1>
        <p className="text-muted-foreground">
          {user?.role === "ceo"
            ? "Executive overview of company performance and analytics."
            : user?.role === "constructor" 
            ? "View your assigned projects and update task progress."
            : user?.role === "project_manager"
            ? "Manage projects, tasks, and team assignments."
            : "Here's what's happening with your account."
          }
        </p>
      </div>

      {/* Quick access for CEO */}
      {user?.role === "ceo" && (
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/dashboard/admin/analytics" className="bg-primary/5 border border-primary/20 rounded-lg p-6 hover:bg-primary/10 transition-colors">
            <TrendingUp className="w-8 h-8 text-primary mb-3" />
            <h2 className="font-semibold mb-1">Company Analytics</h2>
            <p className="text-sm text-muted-foreground">View company-wide performance metrics and trends.</p>
          </Link>
          <Link to="/dashboard/admin/finance" className="bg-primary/5 border border-primary/20 rounded-lg p-6 hover:bg-primary/10 transition-colors">
            <Wallet className="w-8 h-8 text-primary mb-3" />
            <h2 className="font-semibold mb-1">Finance Overview</h2>
            <p className="text-sm text-muted-foreground">Review financial transactions and cash flow.</p>
          </Link>
          <Link to="/dashboard/admin/branches" className="bg-primary/5 border border-primary/20 rounded-lg p-6 hover:bg-primary/10 transition-colors">
            <Building2 className="w-8 h-8 text-primary mb-3" />
            <h2 className="font-semibold mb-1">Organization</h2>
            <p className="text-sm text-muted-foreground">View branches, teams, and organizational structure.</p>
          </Link>
        </div>
      )}

      {/* Quick access for Constructor/Project Manager */}
      {(user?.role === "constructor" || user?.role === "project_manager") && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
          <h2 className="font-semibold mb-2">
            {user?.role === "constructor" ? "My Projects" : "Project Management"}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {user?.role === "constructor" 
              ? "View projects you're assigned to and update your task progress."
              : "Create projects, assign tasks, and manage your team."
            }
          </p>
          <Link to="/dashboard/admin/projects">
            <Button>
              Go to Projects <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      )}

      {/* Agent Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.leads.total}</p>
                <p className="text-xs text-muted-foreground">Total Leads</p>
              </div>
            </div>
            <p className="text-xs text-gold">{stats.leads.new} new</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.deals.total}</p>
                <p className="text-xs text-muted-foreground">Active Deals</p>
              </div>
            </div>
            <p className="text-xs text-green-600">{stats.deals.won} closed</p>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.properties}</p>
                <p className="text-xs text-muted-foreground">Listings</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gold/20 rounded-sm flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="text-2xl font-bold">K{(stats.total_revenue / 1000).toFixed(0)}K</p>
                <p className="text-xs text-muted-foreground">Revenue</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Saved Properties */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl">Saved Properties</h2>
          <Link to="/properties" className="text-sm text-primary hover:underline">
            Browse more <ChevronRight className="w-4 h-4 inline" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card rounded-sm overflow-hidden animate-pulse">
                <div className="h-40 bg-muted"></div>
                <div className="p-4">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-6 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : savedProperties.length === 0 ? (
          <div className="text-center py-12 bg-muted rounded-sm">
            <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-medium mb-2">No saved properties yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Start exploring and save your favorites!</p>
            <Link to="/properties">
              <Button variant="outline">Browse Properties</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedProperties.slice(0, 6).map((property) => (
              <Link 
                key={property.property_id}
                to={`/properties/${property.property_id}`}
                className="bg-card border border-border rounded-sm overflow-hidden hover:border-primary/30 transition-colors"
              >
                <div className="h-32 relative">
                  <img
                    src={property.images?.[0] || "https://images.unsplash.com/photo-1746458258536-b9ee5db20a73"}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {property.city}, {property.state}
                  </p>
                  <h3 className="font-medium line-clamp-1">{property.title}</h3>
                  <p className="font-mono text-primary font-semibold">
                    {formatPrice(property.price, property.listing_type)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardOverview;
